import { Box, Divider, Input, Text } from "@wix/design-system";
import lodash from "lodash";
import React, { useContext, useRef } from "react";
import { ModalServiceContext, RicosContext } from "./../../ricos-context";
import { useNodeAttrs } from "./../../_shared/use-node-attrs";
import { ButtonColorPicker } from "./../components/button-color-picker";
import { buttonsModals } from "./../constants";
import { ButtonColorSwatches, SliderWithInput } from "./../../ui-components";
import { ColorPickerLiveSiteColorHint } from "./../../ui-components/color-picker/button-color-swatches/live-site-color-hint";
import {
  DESIGN_SETTINGS_BACKGROUND_COLOR_MORE_BUTTON,
  DESIGN_SETTINGS_BACKGROUND_COLOR_PREDEFINED_SWATCH,
  DESIGN_SETTINGS_BACKGROUND_COLOR_SWATCHES,
  DESIGN_SETTINGS_BORDER_COLOR_MORE_BUTTON,
  DESIGN_SETTINGS_BORDER_COLOR_PREDEFINED_SWATCH,
  DESIGN_SETTINGS_BORDER_COLOR_SWATCHES,
  DESIGN_SETTINGS_BORDER_WIDTH_INPUT,
  DESIGN_SETTINGS_BORDER_WIDTH_SLIDER,
  DESIGN_SETTINGS_CORNER_RADIUS_INPUT,
  DESIGN_SETTINGS_CORNER_RADIUS_SLIDER,
  DESIGN_SETTINGS_TEXT_COLOR_MORE_BUTTON,
  DESIGN_SETTINGS_TEXT_COLOR_PREDEFINED_SWATCH,
  DESIGN_SETTINGS_TEXT_COLOR_SWATCHES
} from "../data-hooks";
const DesignComponent = ({
  stylesData,
  config,
  onChange,
  defaultBackgroundColor,
  defaultTextColor,
  nodeId,
  nodeType,
  logBi
}) => {
  const { t, isMobile, environment } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext);
  const { pmNodeAttrs } = useNodeAttrs(nodeId, nodeType);
  const backgroundMoreButtonRef = useRef(null);
  const textMoreButtonRef = useRef(null);
  const borderMoreButtonRef = useRef(null);
  const handleOnChange = (data) => {
    onChange({
      border: { ...stylesData.border, ...data.border },
      colors: { ...stylesData.colors, ...data.colors }
    });
  };
  const showLiveSiteHint = environment !== "liveSite";
  const openColorPicker = (colorType) => {
    const referenceElement = getButtonRef(colorType);
    const modalId = buttonsModals.colorPicker;
    modalService.togglePluginPanel({
      id: modalId,
      Component: ButtonColorPicker,
      componentProps: {
        modalId,
        nodeId,
        nodeType,
        colorType,
        referenceElement,
        config,
        defaultTextColor,
        defaultBackgroundColor,
        onColorSelected: onColorSelected(colorType),
        onColorAdded: onColorAdded(colorType)
      },
      isMobile,
      positioning: {
        referenceElement,
        placement: "left"
      },
      type: void 0,
      onClickOutside: lodash.noop,
      focusReturnElement: referenceElement ?? void 0
    });
  };
  const onColorSelected = (colorType) => (color) => handleOnChange({ colors: { [colorType]: color } });
  const currentColor = (colorType) => pmNodeAttrs?.styles?.colors?.[colorType];
  const onColorChange = (colorType) => (color) => handleOnChange({ colors: { [colorType]: color } });
  const onColorAdded = (colorType) => (color) => {
    if (colorType === "background") {
      return config.onBackgroundColorAdded?.(color);
    } else if (colorType === "text") {
      return config.onTextColorAdded?.(color);
    } else {
      return config.onBorderColorAdded?.(color);
    }
  };
  const getButtonRef = (colorType) => {
    if (colorType === "background") {
      return backgroundMoreButtonRef.current;
    } else if (colorType === "text") {
      return textMoreButtonRef.current;
    } else {
      return borderMoreButtonRef.current;
    }
  };
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", width: isMobile ? "100%" : void 0, overflowX: "auto" }, /* @__PURE__ */ React.createElement(Section, null, /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3", paddingTop: "SP2" }, /* @__PURE__ */ React.createElement(
    ButtonColorSwatches,
    {
      label: t("ButtonModal_Background_Color"),
      color: currentColor("background"),
      onChange: (color) => {
        onColorChange("background")(color);
        logBi({ dataHook: DESIGN_SETTINGS_BACKGROUND_COLOR_PREDEFINED_SWATCH, value: color });
      },
      dataHook: DESIGN_SETTINGS_BACKGROUND_COLOR_SWATCHES,
      onMorePress: () => {
        openColorPicker("background");
        logBi({ dataHook: DESIGN_SETTINGS_BACKGROUND_COLOR_MORE_BUTTON });
      },
      defaultColor: defaultBackgroundColor,
      moreButtonRef: backgroundMoreButtonRef
    }
  ), showLiveSiteHint && /* @__PURE__ */ React.createElement(ColorPickerLiveSiteColorHint, null), /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      min: 0,
      max: 30,
      onChange: (radius) => handleOnChange({ border: { radius } }),
      label: t("ButtonModal_Radius_Input"),
      sliderDataHook: DESIGN_SETTINGS_CORNER_RADIUS_SLIDER,
      inputDataHook: DESIGN_SETTINGS_CORNER_RADIUS_INPUT,
      value: stylesData.border.radius,
      suffix: /* @__PURE__ */ React.createElement(Input.Affix, null, t("Common_Units_PX")),
      logBi
    }
  ))), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(Section, { title: t("ButtonModal_Text_Section") }, /* @__PURE__ */ React.createElement(
    ButtonColorSwatches,
    {
      label: t("ButtonModal_Text_Color"),
      color: currentColor("text"),
      onChange: (color) => {
        onColorChange("text")(color);
        logBi({ dataHook: DESIGN_SETTINGS_TEXT_COLOR_PREDEFINED_SWATCH, value: color });
      },
      dataHook: DESIGN_SETTINGS_TEXT_COLOR_SWATCHES,
      defaultColor: defaultTextColor,
      onMorePress: () => {
        openColorPicker("text");
        logBi({ dataHook: DESIGN_SETTINGS_TEXT_COLOR_MORE_BUTTON });
      },
      moreButtonRef: textMoreButtonRef
    }
  )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(Section, { title: t("ButtonModal_Border_Section") }, /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3", paddingTop: "SP2" }, /* @__PURE__ */ React.createElement(
    ButtonColorSwatches,
    {
      label: t("ButtonModal_Border_Color"),
      color: currentColor("border"),
      onChange: (color) => {
        onColorChange("border")(color);
        logBi({ dataHook: DESIGN_SETTINGS_BORDER_COLOR_PREDEFINED_SWATCH, value: color });
      },
      dataHook: DESIGN_SETTINGS_BORDER_COLOR_SWATCHES,
      onMorePress: () => {
        openColorPicker("border");
        logBi({ dataHook: DESIGN_SETTINGS_BORDER_COLOR_MORE_BUTTON });
      },
      defaultColor: void 0,
      moreButtonRef: borderMoreButtonRef
    }
  ), /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      min: 0,
      max: 15,
      onChange: (width) => handleOnChange({ border: { width } }),
      label: t("ButtonModal_Width_Input"),
      sliderDataHook: DESIGN_SETTINGS_BORDER_WIDTH_SLIDER,
      inputDataHook: DESIGN_SETTINGS_BORDER_WIDTH_INPUT,
      value: stylesData.border.width,
      suffix: /* @__PURE__ */ React.createElement(Input.Affix, null, t("Common_Units_PX")),
      logBi
    }
  ))));
};
const Section = ({ title, children }) => {
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical" }, title && /* @__PURE__ */ React.createElement(Box, { padding: "SP3", paddingBottom: "SP2" }, /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, title)), /* @__PURE__ */ React.createElement(Box, { direction: "vertical", padding: "SP3", paddingTop: "SP1", gap: "SP3" }, children));
};
export {
  DesignComponent
};
//# sourceMappingURL=design-component.js.map
